import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ORGANIZATIONS_LOGO_URL } from 'shared/constants';
import { listOrganizations } from '@/api/organization.api.private';
import StaffAPI from '@/api/staff.api';
import authService from '@/lib/auth';
import { USER_PERMISSIONS } from 'shared/constants/auth.constants';
import { useToast } from '@/components/ui/use-toast';
import { formatLongDateTime } from 'shared/utils/date.utils';
import { PencilIcon, PlusCircle, Search } from 'lucide-react';
import OrganizationForm from './components/organization-form';
import SubscribeMeAction from './components/subscribe-me-action';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Link } from 'react-router-dom';
import { Loader } from '@/components/loader';

const CLASSIFICATION_BADGE_TYPES = {
  internal: 'destructive',
  trial: 'secondary',
  onboarding: 'secondary',
};

export default function Organizations() {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [myPermissionByOrgId, setMyPermissionByOrgId] = useState(() => new Map());
  const isFetched = useRef(false);
  const { toast } = useToast();
  // Prefer the JWT-decoded id (always present when authenticated, matches
  // refreshAccessToken's lookup); fall back to state.user if available.
  const meId = authService.state?.tokenDetails?.id || authService.state?.user?.id || null;

  useEffect(() => {
    if (!isFetched.current) {
      isFetched.current = true;
      fetchData();
    }
  }, []);

  useEffect(() => {
    if (!meId) return;
    let cancelled = false;
    StaffAPI.listSubscriptions({ userId: meId })
      .then(res => {
        if (cancelled) return;
        if (res.error) {
          // Surface once on the initial fetch so the admin knows the
          // "subscribed" indicators won't be reliable. Per-row Subscribe
          // attempts will still report duplicate-create from the backend.
          toast({
            title: 'Could not load your subscriptions',
            description:
              res.data?.message ||
              res.error.message ||
              'The "Already subscribed" indicators may be missing.',
            variant: 'destructive',
          });
          return;
        }
        const entries = (res.data?.subscriptions ?? [])
          .filter(sub => sub.organizationId)
          .map(sub => [String(sub.organizationId), sub.permissions]);
        setMyPermissionByOrgId(new Map(entries));
      })
      .catch(() => {
        // Network-level failure — same UX as the error branch above.
        if (!cancelled) {
          toast({
            title: 'Could not load your subscriptions',
            description: 'The "Already subscribed" indicators may be missing.',
            variant: 'destructive',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [meId, toast]);

  const handleSubscribed = useCallback((organizationId, permissions) => {
    setMyPermissionByOrgId(prev => {
      const next = new Map(prev);
      next.set(String(organizationId), permissions);
      return next;
    });
  }, []);

  const fetchData = async ({ isRetry = false } = {}) => {
    setError(null);
    setLoading(true);
    try {
      const { data, error } = await listOrganizations();

      if (error) {
        const status = error.response?.status;
        if (status === 403 && !isRetry) {
          const refreshed = await authService.refreshAccessToken();
          if (refreshed) {
            return fetchData({ isRetry: true });
          }
        }
        setError(error);
      } else if (data) {
        setOrganizations(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
  };

  const renderContent = () => {
    if (loading) {
      return <Loader />;
    }

    if (error) {
      return <div>Error loading organizations</div>;
    }

    const filteredOrganizations = organizations?.filter(organization =>
      organization.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Logo</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Classification</TableHead>
            <TableHead>Domain</TableHead>
            <TableHead>Updated At</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredOrganizations?.map(organization => (
            <TableRow key={organization.id} className="group">
              <TableCell>
                <div className="p-1 mr-4 rounded-md" style={{ background: '#4c5675' }}>
                  <img
                    src={`${ORGANIZATIONS_LOGO_URL}web/${organization.logo}`}
                    alt="logo"
                    width={250}
                    className="object-contain h-8"
                  />
                </div>
              </TableCell>
              <TableCell>{organization.name}</TableCell>
              <TableCell>
                <Badge variant={CLASSIFICATION_BADGE_TYPES[organization.classification]}>
                  {organization.classification}
                </Badge>
              </TableCell>
              <TableCell>
                {organization.domain.map(domain => (
                  <Badge key={domain} className="mr-1 mb-1">
                    {domain}
                  </Badge>
                ))}
              </TableCell>
              <TableCell>
                {formatLongDateTime({ date: organization.updatedAt, locale: 'en-US' })}
              </TableCell>
              <TableCell className="w-2">
                <div className="flex items-center gap-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {myPermissionByOrgId.get(String(organization.id)) ===
                        USER_PERMISSIONS.ROOT ? (
                          <Link to={`/dashboard/organizations/${organization.id}`}>
                            <Button variant="ghost" aria-label="Edit">
                              <PencilIcon />
                            </Button>
                          </Link>
                        ) : (
                          <Button
                            variant="ghost"
                            aria-disabled="true"
                            aria-label="Edit"
                            className="opacity-30 cursor-not-allowed hover:bg-transparent"
                            onClick={e => e.preventDefault()}>
                            <PencilIcon />
                          </Button>
                        )}
                      </TooltipTrigger>
                      <TooltipContent>
                        {myPermissionByOrgId.get(String(organization.id)) === USER_PERMISSIONS.ROOT
                          ? 'Edit'
                          : 'Requires a ROOT subscription to this organization'}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {meId ? (
                    <SubscribeMeAction
                      meId={meId}
                      organizationId={organization.id}
                      organizationName={organization.name}
                      isSubscribed={myPermissionByOrgId.has(String(organization.id))}
                      onSubscribed={handleSubscribed}
                    />
                  ) : null}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <>
      <Card>
        <div className="flex justify-between">
          <CardHeader>
            <CardTitle>Organizations</CardTitle>
            <CardDescription>List of Organizations</CardDescription>
          </CardHeader>
          <div className="flex mb-4 mr-5 w-1/3 self-center justify-self-end gap-2">
            <Search className="self-center" />
            <Input placeholder="Search by name" value={searchQuery} onChange={handleSearchChange} />
          </div>
        </div>

        <OrganizationForm
          organizationList={organizations && organizations?.map(({ id, name }) => ({ id, name }))}
          icon={PlusCircle}
          callback={fetchData}
        />

        <CardContent>{renderContent()}</CardContent>
      </Card>
    </>
  );
}
